import { useTranslation } from "react-i18next";
import { Button, Spinner, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useState } from "react";
import classNames from "classnames";
import { useNavigateWithQuery } from "src/Hooks/useNavigateWithQuery";
import TooltipWrapper from "src/components/Common/TooltipWrapper";
import DeployIcon from "src/assets/images/instance-deploy.png";
import SettingIcon from "src/assets/images/setting.png";
import YamcsWebInterfaceIcon from "src/assets/images/chart.png";
import EditIcon from "src/assets/images/edit.png";
import ManageTestSequenceTagIcon from "src/assets/images/manage-test-sequence-tag.png";
import DownloadIcon from "src/assets/images/download.png";
import SequenceVisualizeIcon from "src/assets/images/sequence-visualize-grafana.png";

const ButtonCollapse = ({ handleCollapse, isCollapsed }) => {
  return (
    <Button outline size="sm" className="px-1 btn-collapse height-by-px-36" onClick={handleCollapse}>
      <i className={`bi bi-chevron-${isCollapsed ? "down" : "up"} f-s-14 fw-bold text-dark`} />
    </Button>
  );
};

const SingleDashboard = ({ handleClick, isReady, isLoading, isShow = true, id }) => {
  const { t } = useTranslation("flight-simulator");
  return isShow ? (
    isLoading ? (
      <Spinner size="sm" color="secondary" className="mx-1" />
    ) : (
      <TooltipWrapper id={`dashboard-${id}`} tooltipText={t("list.table.dashboard")}>
        <button
          className="btn btn-blank px-1 border-0 py-0"
          onClick={() => handleClick()}
          onFocus={(e) => {
            // immediately blurs after focus
            e.target.blur();
          }}
        >
          <i className={`bi bi-speedometer2 ${isReady ? "text-success" : "text-dark"} f-s-20`} />
        </button>
      </TooltipWrapper>
    )
  ) : null;
};

const ButtonSequenceVisualize = ({ handleClick, isReady = false, isLoading, isShow = true, id }) => {
  const { t } = useTranslation("sequence");
  return isShow ? (
    isLoading ? (
      <Spinner size="sm" color="secondary" className="mx-1" />
    ) : (
      <TooltipWrapper id={`sequence-visualize-${id}`} tooltipText={t("list.table.sequence_visualize")}>
        <button
          className="btn btn-blank border-0 p-y-8 px-0"
          onClick={(e) => handleClick(e)}
          onFocus={(e) => {
            // immediately blurs after focus
            e.target.blur();
          }}
        >
          <img
            src={SequenceVisualizeIcon}
            className="width-by-px-24 text-dark-filter prevent-img-context-menu"
            alt="Sequence Visualize icon"
            onContextMenu={(e) => e.preventDefault()}
          />
        </button>
      </TooltipWrapper>
    )
  ) : null;
};

const ButtonDownload = ({ url = "", id, handleDownload, icon, translation = "" }) => {
  const { t } = useTranslation(translation || "test-package");
  return !!url ? (
    <TooltipWrapper id={`download-${id}`} tooltipText={t("detail.download")}>
      <a
        href={url}
        onClick={(e) => {
          if (handleDownload) {
            e.preventDefault();
            handleDownload();
          }
        }}
        className="d-inline-block p-a-8"
      >
        {icon || <img src={DownloadIcon} className="width-by-px-24 text-dark-filter" alt="Download icon" />}
      </a>
    </TooltipWrapper>
  ) : null;
};

const ButtonCopy = ({ isShow = true, toUrl, id, target = "_self", prefix = "", noChangeCUrrentPath = false }) => {
  const { t } = useTranslation("flight-simulator");
  const navigateWithQuery = useNavigateWithQuery();
  return isShow ? (
    <TooltipWrapper id={`copy-${prefix}-${id}`} tooltipText={t("list.table.copy")}>
      <button
        onClick={() =>
          navigateWithQuery({
            newPath: toUrl,
            queryUpdates: { id },
            target,
            noChangeCUrrentPath,
          })
        }
        className="btn btn-blank p-0 border-0"
      >
        <i className="bi bi-copy f-s-20 text-dark" />
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonDetail = ({
  isShow = true,
  toUrl,
  id,
  target = "_self",
  prefix = "",
  noChangeCUrrentPath = false,
  icon,
  translation = "project-instance",
}) => {
  const { t } = useTranslation([translation]);
  const navigateWithQuery = useNavigateWithQuery();
  return isShow ? (
    <TooltipWrapper id={`detail-${prefix}-${id}`} tooltipText={t("list.table.detail")}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigateWithQuery({
            newPath: toUrl,
            queryUpdates: { id },
            target,
            noChangeCUrrentPath,
          });
        }}
        className="btn btn-blank border-0 p-y-8 px-0"
      >
        {icon || (
          <img
            src={SettingIcon}
            className="width-by-px-24 text-dark-filter user-select-none prevent-img-context-menu"
            alt="Instance Detail icon"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        )}
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonDeploy = ({
  isShow = true,
  handleClick,
  id,
  disabled,
  tooltipText = "list.table.deploy",
  translation = "project-instance",
}) => {
  const { t } = useTranslation(translation);
  return isShow ? (
    <TooltipWrapper id={`deploy-${id}`} tooltipText={t(tooltipText)}>
      <div className={disabled ? "d-inline-block" : ""}>
        <button
          className="btn btn-blank border-0 p-y-8 px-0"
          onClick={() => handleClick()}
          onFocus={(e) => {
            // immediately blurs after focus
            e.target.blur();
          }}
          type="button"
          disabled={disabled}
        >
          <img
            src={DeployIcon}
            className="width-by-px-24 text-dark-filter prevent-img-context-menu"
            alt="Instance Deploy icon"
            onContextMenu={(e) => e.preventDefault()}
          />
        </button>
      </div>
    </TooltipWrapper>
  ) : null;
};

const ButtonManageTestSequenceTag = ({ isShow = true, handleClick, id }) => {
  const { t } = useTranslation("test-package");
  return isShow ? (
    <TooltipWrapper id={`manage_test_sequence_tag-${id}`} tooltipText={t("list.table.manage_test_sequence_tag")}>
      <button
        className="btn btn-blank border-0 p-y-8 px-0"
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <img
          src={ManageTestSequenceTagIcon}
          className="width-by-px-24 text-dark-filter prevent-img-context-menu"
          alt="Manage Test Sequence Tag icon"
          onContextMenu={(e) => e.preventDefault()}
        />
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonEdit = ({ isShow, handleClick, id, disabled, tooltipText = "edit", translation = "project-instance" }) => {
  const { t } = useTranslation(translation);
  return isShow ? (
    <TooltipWrapper id={`edit-${id}`} tooltipText={t(tooltipText)}>
      <div className={disabled ? "d-inline-block" : ""}>
        <button
          className="btn btn-blank border-0 p-y-8 px-0"
          onClick={() => handleClick()}
          onFocus={(e) => {
            // immediately blurs after focus
            e.target.blur();
          }}
          disabled={disabled}
        >
          <img
            src={EditIcon}
            className="width-by-px-24 text-dark-filter prevent-img-context-menu"
            alt="Edit icon"
            onContextMenu={(e) => e.preventDefault()}
          />
        </button>
      </div>
    </TooltipWrapper>
  ) : null;
};

const ButtonYamcsWebInterface = ({ isShow, handleClick, id }) => {
  const { t } = useTranslation("project-instance");
  return isShow ? (
    <TooltipWrapper id={`yamcs-${id}`} tooltipText={t("list.table.yamcs_web_interface")}>
      <button
        className="btn btn-blank border-0 p-y-8 px-0"
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <img src={YamcsWebInterfaceIcon} className="width-by-px-24 text-dark-filter" alt="Yamcs Web Interface icon" />
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonRetry = ({ isShow = true, toUrl, id, target = "_self", prefix = "", noChangeCUrrentPath = false }) => {
  const { t } = useTranslation("flight-simulator");
  const navigateWithQuery = useNavigateWithQuery();
  return isShow ? (
    <TooltipWrapper id={`retry-${prefix}-${id}`} tooltipText={t("new.retry")}>
      <button
        onClick={() =>
          navigateWithQuery({
            newPath: toUrl,
            queryUpdates: { id },
            target,
            noChangeCUrrentPath,
          })
        }
        className="btn btn-blank p-0 border-0"
      >
        <i className="bi bi-arrow-clockwise f-s-20 text-dark" />
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonFileViewer = ({ handleClick, id, className }) => {
  const { t } = useTranslation("flight-simulator");
  return (
    <TooltipWrapper id={`fileview-${id}`} tooltipText={t("list.table.display_data")}>
      <button
        className={classNames("btn btn-blank border-0 p-0", className)}
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <i className="bi bi-file-earmark-text f-s-20 text-dark"></i>
      </button>
    </TooltipWrapper>
  );
};

const ButtonInputView = ({ handleClick, id, className }) => {
  const { t } = useTranslation("flight-simulator");
  return (
    <TooltipWrapper id={`inputView-${id}`} tooltipText={t("list.table.display_data")}>
      <button className={classNames("btn btn-blank border-0 p-0", className)} onClick={() => handleClick()}>
        <i className="bi bi-file-earmark-text f-s-20 text-dark"></i>
      </button>
    </TooltipWrapper>
  );
};
const ButtonSimId = ({ handleClick, children = <></>, className }) => {
  return (
    <button onClick={() => handleClick()} className={classNames("bg-transparent p-0 border-0", className)}>
      {children}
    </button>
  );
};

const ButtonErrorWarning = ({ isShow = false, errorContent, id }) => {
  return isShow ? (
    <TooltipWrapper id={`serie-item-${id}`} tooltipText={errorContent}>
      <i className="bi text-danger bi-exclamation-triangle f-s-20 px-1" />
    </TooltipWrapper>
  ) : null;
};

const ButtonResultDisplay = ({ isShow, handleClick, id }) => {
  const { t } = useTranslation("gnc-simulator");
  return isShow ? (
    <TooltipWrapper id={`result-display-${id}`} tooltipText={t("list.table.display_data")}>
      <button
        className="btn btn-blank border-0 p-0"
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <i className="bi bi-ui-checks-grid f-s-20 text-dark"></i>
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonTerminalSimulation = ({ isShow, handleSubmit, id }) => {
  const { t } = useTranslation("flight-simulator");
  const [toggle, setToggle] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);

  return isShow ? (
    <>
      <TooltipWrapper id={`terminal-button-${id}`} tooltipText={t("list.table.terminal")}>
        <button
          className="btn btn-blank border-0 p-0"
          onClick={() => setToggle(true)}
          onFocus={(e) => {
            // immediately blurs after focus
            e.target.blur();
          }}
        >
          <i className="bi bi-stop-circle f-s-20 text-dark"></i>
        </button>
      </TooltipWrapper>

      <Modal isOpen={toggle} toggle={() => setToggle(false)} backdrop="static" size="md" centered>
        <ModalHeader>{t("list.table.terminal_popup_title")}</ModalHeader>
        <ModalBody>
          <div>{t("list.table.terminal_popup_content")}</div>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-secondary d-inline-block width-by-px-75" onClick={() => setToggle(false)}>
            {t("list.table.no")}
          </button>
          <button
            className="btn btn-danger d-inline-block width-by-px-75"
            onClick={() => {
              setIsSubmit(true);
              handleSubmit?.(id, () => {
                setIsSubmit(false);
                setToggle(false);
              });
            }}
            disabled={isSubmit}
          >
            {t("list.table.yes")}
          </button>
        </ModalFooter>
      </Modal>
    </>
  ) : null;
};

const ButtonReferenceHistory = ({ handleClick, id }) => {
  const { t } = useTranslation("gnc-simulator-6dof");
  return (
    <TooltipWrapper id={`reference-history-${id}`} tooltipText={t("list.table.reference_history.title")}>
      <button
        className="btn btn-blank border-0 p-0 reference-history-button"
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <i className="pi pi-history f-s-16 text-dark"></i>
      </button>
    </TooltipWrapper>
  );
};

export {
  ButtonCollapse,
  SingleDashboard,
  ButtonDownload,
  ButtonCopy,
  ButtonRetry,
  ButtonFileViewer,
  ButtonErrorWarning,
  ButtonResultDisplay,
  ButtonTerminalSimulation,
  ButtonReferenceHistory,
  ButtonInputView,
  ButtonSimId,
  ButtonDetail,
  ButtonDeploy,
  ButtonYamcsWebInterface,
  ButtonEdit,
  ButtonManageTestSequenceTag,
  ButtonSequenceVisualize,
};
